/**
 * @engine/physics2d - Sequential Impulse Constraint Solver
 *
 * Resolves velocity and position constraints from contact manifolds.
 * Uses warm-starting for stability and Baumgarte stabilization for
 * position correction. Position solver re-evaluates separation each
 * iteration using local contact anchors (Box2D-style).
 *
 * GPU-portable: all operations are per-constraint, no global state.
 */

import type { PhysicsBodyState, CollisionManifold, ContactPoint2D, PhysVec2 } from '../types';
import { BodyType, SOLVER_DEFAULTS } from '../types';

// ============================================================================
// SOLVER STATE
// ============================================================================

/** Per-contact pre-computed data */
interface SolverContact {
  rA: PhysVec2;        // contact offset from body A center (world)
  rB: PhysVec2;        // contact offset from body B center (world)
  localAnchorA: PhysVec2;  // contact point in body A local space
  localAnchorB: PhysVec2;  // contact point in body B local space
  normalMass: number;  // effective mass along normal
  tangentMass: number; // effective mass along tangent
  velocityBias: number; // restitution + penetration bias
  normalImpulse: number;
  tangentImpulse: number;
}

/** Pre-computed constraint data for solver iterations */
export interface VelocityConstraint {
  manifold: CollisionManifold;
  bodyA: PhysicsBodyState;
  bodyB: PhysicsBodyState;
  contacts: SolverContact[];
  normal: PhysVec2;
  tangent: PhysVec2;
  friction: number;
  restitution: number;
}

// ============================================================================
// SOLVER
// ============================================================================

/**
 * Solve velocity constraints for all contact manifolds.
 * Returns velocity constraints (with local anchors) for position solving.
 */
export function solveVelocities(
  manifolds: CollisionManifold[],
  bodies: PhysicsBodyState[],
  velocityIterations: number,
  dt: number,
): VelocityConstraint[] {
  const invDt = dt > 0 ? 1 / dt : 0;
  const baumgarte = SOLVER_DEFAULTS.baumgarte;
  const slop = SOLVER_DEFAULTS.linearSlop;

  // Prepare constraints
  const constraints: VelocityConstraint[] = [];

  for (const m of manifolds) {
    const bodyA = bodies[m.bodyIndexA];
    const bodyB = bodies[m.bodyIndexB];
    if (!bodyA || !bodyB) continue;

    const nx = m.normal.x;
    const ny = m.normal.y;
    const tx = -ny; // tangent perpendicular to normal
    const ty = nx;

    // Combine materials from manifold
    const friction = m.friction;
    const restitution = m.restitution;

    // Precompute body rotations for local anchor transform
    const cosA = Math.cos(bodyA.rotation);
    const sinA = Math.sin(bodyA.rotation);
    const cosB = Math.cos(bodyB.rotation);
    const sinB = Math.sin(bodyB.rotation);

    const contacts: SolverContact[] = [];

    for (let i = 0; i < m.contactCount; i++) {
      const cp = m.contacts[i];

      const rAx = cp.point.x - bodyA.position.x;
      const rAy = cp.point.y - bodyA.position.y;
      const rBx = cp.point.x - bodyB.position.x;
      const rBy = cp.point.y - bodyB.position.y;

      // Store contact in local body space for position solver re-evaluation
      const localAnchorA: PhysVec2 = {
        x:  rAx * cosA + rAy * sinA,
        y: -rAx * sinA + rAy * cosA,
      };
      const localAnchorB: PhysVec2 = {
        x:  rBx * cosB + rBy * sinB,
        y: -rBx * sinB + rBy * cosB,
      };

      // Effective mass for normal constraint:
      // 1/mA + 1/mB + (rA×n)² /IA + (rB×n)² /IB
      const rnA = rAx * ny - rAy * nx;
      const rnB = rBx * ny - rBy * nx;
      const normalMass = bodyA.invMass + bodyB.invMass +
        rnA * rnA * bodyA.invInertia + rnB * rnB * bodyB.invInertia;

      const rtA = rAx * ty - rAy * tx;
      const rtB = rBx * ty - rBy * tx;
      const tangentMass = bodyA.invMass + bodyB.invMass +
        rtA * rtA * bodyA.invInertia + rtB * rtB * bodyB.invInertia;

      // Relative velocity at contact
      const relVx = (bodyB.linearVelocity.x + (-bodyB.angularVelocity * rBy)) -
                     (bodyA.linearVelocity.x + (-bodyA.angularVelocity * rAy));
      const relVy = (bodyB.linearVelocity.y + (bodyB.angularVelocity * rBx)) -
                     (bodyA.linearVelocity.y + (bodyA.angularVelocity * rAx));
      const relVelNormal = relVx * nx + relVy * ny;

      // Restitution bias
      let velocityBias = 0;
      if (relVelNormal < -1.0) { // threshold
        velocityBias = -restitution * relVelNormal;
      }

      // Velocity-level Baumgarte: add bias to push apart penetrating bodies.
      // This provides immediate penetration recovery during velocity solving,
      // complementing the position solver for robustness.
      if (cp.separation < -slop) {
        velocityBias = Math.max(velocityBias,
          -baumgarte * invDt * (cp.separation + slop));
      }

      contacts.push({
        rA: { x: rAx, y: rAy },
        rB: { x: rBx, y: rBy },
        localAnchorA,
        localAnchorB,
        normalMass: normalMass > 0 ? 1 / normalMass : 0,
        tangentMass: tangentMass > 0 ? 1 / tangentMass : 0,
        velocityBias,
        normalImpulse: cp.normalImpulse, // warm-start
        tangentImpulse: cp.tangentImpulse,
      });
    }

    constraints.push({
      manifold: m,
      bodyA,
      bodyB,
      contacts,
      normal: { x: nx, y: ny },
      tangent: { x: tx, y: ty },
      friction,
      restitution,
    });
  }

  // Warm-start: apply cached impulses
  for (const vc of constraints) {
    for (const c of vc.contacts) {
      const px = vc.normal.x * c.normalImpulse + vc.tangent.x * c.tangentImpulse;
      const py = vc.normal.y * c.normalImpulse + vc.tangent.y * c.tangentImpulse;
      applyImpulse(vc.bodyA, vc.bodyB, c.rA, c.rB, px, py);
    }
  }

  // Iterative solve
  for (let iter = 0; iter < velocityIterations; iter++) {
    for (const vc of constraints) {
      const { bodyA, bodyB, normal, tangent, friction: fric } = vc;

      for (const c of vc.contacts) {
        // Relative velocity at contact
        const relVx = (bodyB.linearVelocity.x - bodyB.angularVelocity * c.rB.y) -
                       (bodyA.linearVelocity.x - bodyA.angularVelocity * c.rA.y);
        const relVy = (bodyB.linearVelocity.y + bodyB.angularVelocity * c.rB.x) -
                       (bodyA.linearVelocity.y + bodyA.angularVelocity * c.rA.x);

        // Tangent impulse (friction)
        const relVelTangent = relVx * tangent.x + relVy * tangent.y;
        let tangentLambda = -relVelTangent * c.tangentMass;

        // Coulomb friction clamp
        const maxFriction = fric * c.normalImpulse;
        const newTangentImpulse = Math.max(-maxFriction,
          Math.min(maxFriction, c.tangentImpulse + tangentLambda));
        tangentLambda = newTangentImpulse - c.tangentImpulse;
        c.tangentImpulse = newTangentImpulse;

        applyImpulse(bodyA, bodyB, c.rA, c.rB,
          tangent.x * tangentLambda, tangent.y * tangentLambda);

        // Normal impulse (non-penetration)
        const relVx2 = (bodyB.linearVelocity.x - bodyB.angularVelocity * c.rB.y) -
                        (bodyA.linearVelocity.x - bodyA.angularVelocity * c.rA.y);
        const relVy2 = (bodyB.linearVelocity.y + bodyB.angularVelocity * c.rB.x) -
                        (bodyA.linearVelocity.y + bodyA.angularVelocity * c.rA.x);
        const relVelNormal = relVx2 * normal.x + relVy2 * normal.y;

        let normalLambda = -(relVelNormal - c.velocityBias) * c.normalMass;

        // Accumulate & clamp (must be non-negative)
        const newNormalImpulse = Math.max(0, c.normalImpulse + normalLambda);
        normalLambda = newNormalImpulse - c.normalImpulse;
        c.normalImpulse = newNormalImpulse;

        applyImpulse(bodyA, bodyB, c.rA, c.rB,
          normal.x * normalLambda, normal.y * normalLambda);
      }
    }
  }

  // Write back impulses for warm-starting next frame
  for (const vc of constraints) {
    for (let i = 0; i < vc.contacts.length; i++) {
      vc.manifold.contacts[i].normalImpulse = vc.contacts[i].normalImpulse;
      vc.manifold.contacts[i].tangentImpulse = vc.contacts[i].tangentImpulse;
    }
  }

  return constraints;
}

/**
 * Solve position constraints (Baumgarte stabilization).
 *
 * Re-evaluates contact separation each iteration by transforming stored
 * local anchors back to world space using current body positions/rotations.
 * This is critical for convergence — without it, stale separation values
 * cause objects to tunnel through each other.
 */
export function solvePositions(
  constraints: VelocityConstraint[],
  bodies: PhysicsBodyState[],
  positionIterations: number,
): void {
  const baumgarte = SOLVER_DEFAULTS.baumgarte;
  const slop = SOLVER_DEFAULTS.linearSlop;
  const maxCorrection = SOLVER_DEFAULTS.maxLinearCorrection;

  for (let iter = 0; iter < positionIterations; iter++) {
    for (const vc of constraints) {
      const bodyA = vc.bodyA;
      const bodyB = vc.bodyB;
      const nx = vc.normal.x;
      const ny = vc.normal.y;

      for (const c of vc.contacts) {
        // Recompute world contact points from local anchors using
        // CURRENT body positions and rotations (which change each iteration)
        const cosA = Math.cos(bodyA.rotation);
        const sinA = Math.sin(bodyA.rotation);
        const worldAx = bodyA.position.x + c.localAnchorA.x * cosA - c.localAnchorA.y * sinA;
        const worldAy = bodyA.position.y + c.localAnchorA.x * sinA + c.localAnchorA.y * cosA;

        const cosB = Math.cos(bodyB.rotation);
        const sinB = Math.sin(bodyB.rotation);
        const worldBx = bodyB.position.x + c.localAnchorB.x * cosB - c.localAnchorB.y * sinB;
        const worldBy = bodyB.position.y + c.localAnchorB.x * sinB + c.localAnchorB.y * cosB;

        // Fresh contact offsets
        const rAx = worldAx - bodyA.position.x;
        const rAy = worldAy - bodyA.position.y;
        const rBx = worldBx - bodyB.position.x;
        const rBy = worldBy - bodyB.position.y;

        // Re-evaluate separation along normal from current positions
        const separation = (worldBx - worldAx) * nx + (worldBy - worldAy) * ny;

        // Position correction (only when penetrating past slop)
        const C = Math.max(-maxCorrection,
          Math.min(0, baumgarte * (separation + slop)));

        const rnA = rAx * ny - rAy * nx;
        const rnB = rBx * ny - rBy * nx;
        const K = bodyA.invMass + bodyB.invMass +
          rnA * rnA * bodyA.invInertia + rnB * rnB * bodyB.invInertia;

        const impulse = K > 0 ? -C / K : 0;
        const px = nx * impulse;
        const py = ny * impulse;

        // Apply position correction
        if (bodyA.bodyType === BodyType.Dynamic) {
          bodyA.position.x -= bodyA.invMass * px;
          bodyA.position.y -= bodyA.invMass * py;
          bodyA.rotation -= bodyA.invInertia * (rAx * py - rAy * px);
        }
        if (bodyB.bodyType === BodyType.Dynamic) {
          bodyB.position.x += bodyB.invMass * px;
          bodyB.position.y += bodyB.invMass * py;
          bodyB.rotation += bodyB.invInertia * (rBx * py - rBy * px);
        }
      }
    }
  }
}

// ============================================================================
// HELPERS
// ============================================================================

function applyImpulse(
  bodyA: PhysicsBodyState,
  bodyB: PhysicsBodyState,
  rA: PhysVec2,
  rB: PhysVec2,
  px: number,
  py: number,
): void {
  if (bodyA.bodyType === BodyType.Dynamic) {
    bodyA.linearVelocity.x -= bodyA.invMass * px;
    bodyA.linearVelocity.y -= bodyA.invMass * py;
    bodyA.angularVelocity -= bodyA.invInertia * (rA.x * py - rA.y * px);
  }
  if (bodyB.bodyType === BodyType.Dynamic) {
    bodyB.linearVelocity.x += bodyB.invMass * px;
    bodyB.linearVelocity.y += bodyB.invMass * py;
    bodyB.angularVelocity += bodyB.invInertia * (rB.x * py - rB.y * px);
  }
}
